import React, { useState, useEffect, useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Separator } from "@/components/ui/separator";
import { Input } from "@/components/ui/input";
// Using native select instead of custom select component
import { Clock, Building2, User, Calendar, Trash2, Edit, FileText, Search, Filter, Eye } from "lucide-react";
import { useFormPersistence, SavedForm } from '@/hooks/useFormPersistence';
import { useAuth } from '@/contexts/AuthContext';
import { useTranslation } from 'react-i18next';

interface DraftFormsListProps {
  onContinueForm: (formId: string) => void;
  onClose?: () => void;
  externalForms?: SavedForm[]; // Optional prop to pass forms from parent
}

export const DraftFormsList: React.FC<DraftFormsListProps> = ({
  onContinueForm,
  onClose,
  externalForms,
}) => {
  const { t } = useTranslation('common');
  const { user } = useAuth();
  const { listForms, deleteForm } = useFormPersistence();

  const [forms, setForms] = useState<SavedForm[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [deletingFormId, setDeletingFormId] = useState<string | null>(null);
  
  // Filter states
  const [companyFilter, setCompanyFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [dateFromFilter, setDateFromFilter] = useState('');
  const [dateToFilter, setDateToFilter] = useState('');
  const [submittedDateFromFilter, setSubmittedDateFromFilter] = useState('');
  const [submittedDateToFilter, setSubmittedDateToFilter] = useState('');

  const loadForms = async () => {
    // If external forms are provided, use them instead of loading
    if (externalForms) {
      console.log('📋 DraftFormsList: Using external forms from parent:', externalForms.length);
      setForms(externalForms);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      // Ensure we're filtering by current user's username
      if (!user?.username) {
        console.warn('⚠️ Uživatel není přihlášen, nelze načíst formuláře');
        setForms([]);
        return;
      }

      console.log('📋 DraftFormsList: Načítání formulářů pro uživatele:', user.username);
      console.log('📋 DraftFormsList: User object:', { 
        username: user.username, 
        full_name: user.full_name, 
        id: user.id 
      });
      
      // Explicitly pass the representative parameter to filter forms
      const allForms = await listForms(user.username);
      
      console.log('📋 DraftFormsList: API vrátilo formulářů před filtrací:', allForms.length);
      allForms.forEach((form, index) => {
        console.log(`📋 DraftFormsList: Form ${index + 1}:`, {
          uuid: form.uuid.slice(0, 8),
          company: form.company_name,
          representative: form.representative,
          status: form.status
        });
      });
      
      // DOČASNÉ FRONTEND FILTROVÁNÍ - odstraníme po opravě backendu
      // Filtruj formuláře podle aktuálního uživatele (fallback pro backend bug)
      const userForms = allForms.filter(form => 
        form.representative === user.username
      );
      
      console.log('📋 DraftFormsList: Po frontend filtraci podle representative:', userForms.length);
      
      // Show all forms (not just drafts)
      console.log('✅ DraftFormsList: Všechny formuláře pro uživatele:', user.username, '→', userForms.length);
      setForms(userForms);
    } catch (error) {
      console.error('❌ Chyba při načítání formulářů:', error);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadForms();
  }, [externalForms]);

  const handleDeleteForm = async (formId: string) => {
    setDeletingFormId(formId);
    try {
      const success = await deleteForm(formId);
      if (success) {
        setForms(forms.filter(f => f.uuid !== formId));
        console.log('✅ Formulář smazán');
      }
    } catch (error) {
      console.error('❌ Chyba při mazání formuláře:', error);
    } finally {
      setDeletingFormId(null);
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('cs-CZ', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'draft':
        return (
          <Badge variant="outline" className="bg-yellow-100 text-yellow-800 border-yellow-300">
            <FileText className="h-3 w-3 mr-1" />
            {t('status.draft')}
          </Badge>
        );
      case 'submitted':
        return (
          <Badge variant="outline" className="bg-orange-100 text-orange-800 border-orange-300">
            <Clock className="h-3 w-3 mr-1" />
            {t('status.submitted')}
          </Badge>
        );
      case 'synced':
        return (
          <Badge variant="outline" className="bg-green-100 text-green-800 border-green-300">
            ✅ {t('status.synced')}
          </Badge>
        );
      case 'error':
        return (
          <Badge variant="outline" className="bg-red-100 text-red-800 border-red-300">
            ❌ {t('status.error', 'Chyba')}
          </Badge>
        );
      default:
        return (
          <Badge variant="outline" className="bg-gray-100 text-gray-800">
            {status}
          </Badge>
        );
    }
  };

  // Filtered forms based on search criteria
  const filteredForms = useMemo(() => {
    return forms.filter(form => {
      // Company name filter - backend now returns correct data
      if (companyFilter && !form.company_name?.toLowerCase().includes(companyFilter.toLowerCase())) {
        return false;
      }
      
      // Status filter
      if (statusFilter !== 'all' && form.status !== statusFilter) {
        return false;
      }
      
      // Creation date filter
      if (dateFromFilter) {
        const createdDate = new Date(form.created_at);
        const fromDate = new Date(dateFromFilter);
        if (createdDate < fromDate) return false;
      }
      
      if (dateToFilter) {
        const createdDate = new Date(form.created_at);
        const toDate = new Date(dateToFilter);
        toDate.setHours(23, 59, 59, 999); // Include the entire day
        if (createdDate > toDate) return false;
      }
      
      // Submitted date filter (only for submitted/synced forms)
      if (submittedDateFromFilter && (form.status === 'submitted' || form.status === 'synced')) {
        const submittedDate = new Date(form.updated_at); // Using updated_at as submission date
        const fromDate = new Date(submittedDateFromFilter);
        if (submittedDate < fromDate) return false;
      }
      
      if (submittedDateToFilter && (form.status === 'submitted' || form.status === 'synced')) {
        const submittedDate = new Date(form.updated_at);
        const toDate = new Date(submittedDateToFilter);
        toDate.setHours(23, 59, 59, 999);
        if (submittedDate > toDate) return false;
      }
      
      return true;
    });
  }, [forms, companyFilter, statusFilter, dateFromFilter, dateToFilter, submittedDateFromFilter, submittedDateToFilter]);

  const clearFilters = () => {
    setCompanyFilter('');
    setStatusFilter('all');
    setDateFromFilter('');
    setDateToFilter('');
    setSubmittedDateFromFilter('');
    setSubmittedDateToFilter('');
  };

  const hasActiveFilters = companyFilter || statusFilter !== 'all' || dateFromFilter || dateToFilter || submittedDateFromFilter || submittedDateToFilter;

  if (isLoading) {
    return (
      <Card className="w-full max-w-4xl mx-auto">
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            {t('forms_list.loading')}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex justify-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <Card className="w-full max-w-4xl mx-auto">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <FileText className="h-5 w-5" />
            Formuláře ({filteredForms.length}/{forms.length})
          </CardTitle>
          {onClose && (
            <Button 
              variant="outline" 
              size="sm"
              onClick={onClose}
            >
              {t('forms_list.close')}
            </Button>
          )}
        </div>
        {user && (
          <p className="text-sm text-slate-600">
            {t('forms_list.representative', { name: user.full_name || user.username })}
          </p>
        )}
      </CardHeader>
      <CardContent>
        {/* Filters */}
        <div className="mb-6 space-y-4">
          <div className="flex items-center gap-2 mb-4">
            <Filter className="h-4 w-4 text-slate-500" />
            <h3 className="font-medium text-slate-900">Filtrování</h3>
            {hasActiveFilters && (
              <Button
                variant="outline"
                size="sm"
                onClick={clearFilters}
                className="ml-auto"
              >
                Vymazat filtry
              </Button>
            )}
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Company name filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Název firmy
              </label>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-slate-400" />
                <Input
                  placeholder="Hledat firmu..."
                  value={companyFilter}
                  onChange={(e) => setCompanyFilter(e.target.value)}
                  className="pl-10"
                />
              </div>
            </div>
            
            {/* Status filter */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Stav
              </label>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full rounded-md border border-slate-300 bg-white px-3 py-2 text-sm shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500"
              >
                <option value="all">Všechny stavy</option>
                <option value="draft">Rozpracováno</option>
                <option value="submitted">Odesláno</option>
                <option value="error">Chyba</option>
              </select>
            </div>
            
            {/* Creation date from */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Vytvořeno od
              </label>
              <Input
                type="date"
                value={dateFromFilter}
                onChange={(e) => setDateFromFilter(e.target.value)}
              />
            </div>
            
            {/* Creation date to */}
            <div>
              <label className="text-sm font-medium text-slate-700 mb-1 block">
                Vytvořeno do
              </label>
              <Input
                type="date"
                value={dateToFilter}
                onChange={(e) => setDateToFilter(e.target.value)}
              />
            </div>
          </div>
          
          {/* Submitted date filters - only show if there are submitted/synced forms */}
          {forms.some(f => f.status === 'submitted' || f.status === 'synced') && (
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Odesláno od
                </label>
                <Input
                  type="date"
                  value={submittedDateFromFilter}
                  onChange={(e) => setSubmittedDateFromFilter(e.target.value)}
                />
              </div>
              
              <div>
                <label className="text-sm font-medium text-slate-700 mb-1 block">
                  Odesláno do
                </label>
                <Input
                  type="date"
                  value={submittedDateToFilter}
                  onChange={(e) => setSubmittedDateToFilter(e.target.value)}
                />
              </div>
            </div>
          )}
        </div>
        
        <Separator className="mb-6" />
        
        {filteredForms.length === 0 ? (
          <div className="text-center py-8">
            <FileText className="h-12 w-12 text-slate-300 mx-auto mb-4" />
            <p className="text-slate-500 mb-2">
              {forms.length === 0 ? 'Nemáte žádné formuláře' : 'Žádné formuláře nevyhovují zvoleným filtrům'}
            </p>
            <p className="text-sm text-slate-400">
              {t('forms_list.auto_save_info')}
            </p>
          </div>
        ) : (
          <div className="space-y-4">
            {filteredForms.map((form) => (
              <div
                key={form.uuid}
                className="border rounded-lg p-4 hover:bg-slate-50 transition-colors"
              >
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2 mb-2">
                      <Building2 className="h-4 w-4 text-slate-500 flex-shrink-0" />
                      <h3 className="font-medium text-slate-900 truncate">
                        {form.company_name}
                      </h3>
                      {getStatusBadge(form.status)}
                    </div>
                    
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2 text-sm text-slate-600">
                      {form.contact_person && (
                        <div className="flex items-center gap-2">
                          <User className="h-3 w-3" />
                          <span className="truncate">{form.contact_person}</span>
                        </div>
                      )}
                      
                      {form.email && (
                        <div className="flex items-center gap-2">
                          <span className="text-slate-400">✉</span>
                          <span className="truncate">{form.email}</span>
                        </div>
                      )}
                      
                      {form.visit_date && (
                        <div className="flex items-center gap-2">
                          <Calendar className="h-3 w-3" />
                          <span>
                            {new Date(form.visit_date).toLocaleDateString('cs-CZ')}
                          </span>
                        </div>
                      )}
                      
                      <div className="flex items-center gap-2">
                        <Clock className="h-3 w-3" />
                        <span>
                          {formatDate(form.updated_at)}
                        </span>
                      </div>
                    </div>
                  </div>
                  
                  <div className="flex items-center gap-2 flex-shrink-0">
                    <Button
                      size="sm"
                      onClick={() => onContinueForm(form.uuid)}
                      className={form.status === 'draft' ? "bg-amber-500 hover:bg-amber-600 text-slate-900" : "bg-orange-500 hover:bg-orange-600 text-white"}
                    >
                      {form.status === 'draft' ? (
                        <><Edit className="h-3 w-3 mr-1" />Pokračovat</>
                      ) : (
                        <><Eye className="h-3 w-3 mr-1" />Zobrazit</>
                      )}
                    </Button>
                    
                    {form.status === 'draft' && (
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleDeleteForm(form.uuid)}
                        disabled={deletingFormId === form.uuid}
                        className="text-red-600 border-red-200 hover:bg-red-50"
                      >
                        {deletingFormId === form.uuid ? (
                          <div className="h-3 w-3 animate-spin rounded-full border-2 border-red-600 border-t-transparent" />
                        ) : (
                          <Trash2 className="h-3 w-3" />
                        )}
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
        
        {filteredForms.length > 0 && (
          <>
            <Separator className="my-6" />
            <div className="flex justify-center">
              <Button
                variant="outline"
                onClick={loadForms}
                disabled={isLoading}
              >
                Obnovit
              </Button>
            </div>
          </>
        )}
      </CardContent>
    </Card>
  );
}; 